import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../core/api/apiDefinitions';
import { checkPermission } from '../../../core/permissons/CheckPermissons';
import { showError } from '../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../router/all_routes';
import CollapseHeader from '../../../core/common/collapse-header';
import AddLetterModule from './AddLetterModule';
import EditLetterModule from './EditLetterModule';
import ViewLetterDetails from './ViewLetterDetails';
import HandoverLetterModule from './HandoverLetterModule';

interface LetterDTO {
  id: number;
  date: string;
  referenceNo: string;
  letterType: string;
  senderDetails: string;
  letterDescription: string;
  status: string;
  handovers: any[];
}

interface User {
  empNumber: string;
  name: string;
}

interface Department {
  id: number;
  name: string;
}

interface GetLettersResponse {
  status: string;
  statusDescription: string;
  data: LetterDTO[];
  total?: number;
}

const navItems = [
  { label: 'Letter Management', icon: 'ti ti-receipt', link: all_routes.letter, permission: 'ROLE_VIEW_LETTER' },
];

const ITEMS_PER_PAGE = 10;

/** Treat these statuses as already handed over (view-only). Adjust to your backend values. */
const isHandedOverStatus = (status?: string) => {
  const s = (status || '').toUpperCase();
  return ['HANDED_OVER', 'HANDOVER', 'DELIVERED', 'COMPLETED'].includes(s);
};

const Letter: React.FC = () => {
  const locationHook = useLocation();
  const navigate = useNavigate();

  const [letters, setLetters] = useState<LetterDTO[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHandoverModal, setShowHandoverModal] = useState(false);

  const [selectedLetterIdForEditView, setSelectedLetterIdForEditView] = useState<number | null>(null);

  // NEW: multi-select IDs for handover (bulk or single)
  const [selectedLetterIds, setSelectedLetterIds] = useState<number[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate('/login');
      return false;
    }
    return token;
  };

  const fetchLetters = async (page: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_VIEW_LETTER')) {
      showError('You do not have permission to view letters.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const payload = { filterType: null, filterValue: '', date: null };
      const res: AxiosResponse = await apiDefinitions.getAllLetters(page, payload);
      if (res.status === 200 && res.data.status === 'S1000') {
        const data: GetLettersResponse = res.data;
        setLetters(data.data || []);
        const total = res.data.total !== undefined ? res.data.total : data.data.length;
        setTotalPages(Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)));

        // reset selection when page changes
        setSelectedLetterIds([]);

        if ((data.data || []).length === 0 && page === 1) {
          setError('No letters found.');
        } else if ((data.data || []).length === 0 && page > 1) {
          setCurrentPage((prev) => Math.max(1, prev - 1));
          setError('No more letters to display.');
        }
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch letters');
      }
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to fetch letters. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const payload = { filterType: null, filterValue: '', date: null };
      const response = await apiDefinitions.getUsers(1, payload);
      if (response.data.status === 'S1000') {
        const usersData = response.data.data.users.map((user: any) => ({
          empNumber: user.empNumber.toString(),
          name: user.name || 'Unknown',
        }));
        setUsers(usersData);
      } else {
        showError('Failed to fetch users: ' + response.data.statusDescription);
      }
    } catch (e: any) {
      showError('Failed to fetch users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartments = async () => {
    setIsLoading(true);
    try {
      const response = await apiDefinitions.getDepartments(1, {});
      if (response.data.status === 'S1000') {
        setDepartments(response.data.data.departments || []);
      } else {
        showError('Failed to fetch departments: ' + response.data.statusDescription);
      }
    } catch (e: any) {
      showError('Failed to fetch departments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLetter = async (payload: {
    date: string;
    referenceNo: string;
    letterType: string;
    senderDetails: string;
    letterDescription?: string;
  }) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_ADD_LETTER')) {
      showError('You do not have permission to add letters.');
      return;
    }
    setIsLoading(true);
    try {
      const formattedPayload = {
        ...payload,
        date: new Date(payload.date).toISOString(),
      };
      const res: AxiosResponse = await apiDefinitions.addLetter(formattedPayload);
      if (res.status === 200 && res.data.status === 'S1000') {
        setShowAddModal(false);
        setCurrentPage(1);
        await fetchLetters(1);
      } else {
        showError(res.data?.statusDescription || 'Failed to add letter');
      }
    } catch (e: any) {
      showError('Failed to add letter. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditLetter = async (payload: {
    id: number;
    date?: string;
    referenceNo?: string;
    letterType?: string;
    senderDetails?: string;
    letterDescription?: string;
  }) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_ADD_LETTER')) {
      showError('You do not have permission to edit letters.');
      return;
    }
    setIsLoading(true);
    try {
      const formattedPayload = {
        ...payload,
        date: payload.date ? new Date(payload.date).toISOString() : undefined,
      };
      const res: AxiosResponse = await apiDefinitions.updateLetter(formattedPayload);
      if (res.status === 200 && res.data.status === 'S1000') {
        setShowEditModal(false);
        setSelectedLetterIdForEditView(null);
        await fetchLetters(currentPage);
      } else {
        showError(res.data?.statusDescription || 'Failed to update letter');
      }
    } catch (e: any) {
      showError('Failed to update letter. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLetter = async (letterId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_ADD_LETTER')) {
      showError('You do not have permission to delete letters.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this letter?')) return;
    setIsLoading(true);
    try {
      const res: AxiosResponse = await apiDefinitions.deleteLetter(letterId);
      if (res.status === 200 && res.data.status === 'S1000') {
        await fetchLetters(currentPage);
      } else {
        showError(res.data?.statusDescription || 'Failed to delete letter');
      }
    } catch (e: any) {
      showError('Failed to delete letter. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // The payload already supports multiple IDs: { letterIds: number[], empNo, departmentId? }
  const handleHandoverLetter = async (payload: { letterIds: number[]; empNo: string; departmentId?: number }) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_HANDOVER_LETTER')) {
      showError('You do not have permission to handover letters.');
      return;
    }
    if (!payload.letterIds || payload.letterIds.length === 0) {
      showError('Please select at least one letter to handover.');
      return;
    }
    setIsLoading(true);
    try {
      const res: AxiosResponse = await apiDefinitions.handoverLetters(payload);
      if (res.status === 200 && res.data.status === 'S1000') {
        setShowHandoverModal(false);
        setSelectedLetterIds([]);
        await fetchLetters(currentPage);
      } else {
        showError(res.data?.statusDescription || 'Failed to handover letter(s)');
      }
    } catch (e: any) {
      showError('Failed to handover letter(s). Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLetters(currentPage);
    fetchUsers();
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // ====== Selection helpers ======
  const toggleSelectAllOnPage = () => {
    const pageSelectableIds = letters.filter(l => !isHandedOverStatus(l.status)).map(l => l.id);
    const allSelected = pageSelectableIds.length > 0 && pageSelectableIds.every(id => selectedLetterIds.includes(id));
    if (allSelected) {
      // unselect only selectable ones
      setSelectedLetterIds(prev => prev.filter(id => !pageSelectableIds.includes(id)));
    } else {
      // add all selectable, keep already selected ones, unique
      setSelectedLetterIds(prev => Array.from(new Set([...prev, ...pageSelectableIds])));
    }
  };

  const isRowSelected = (id: number) => selectedLetterIds.includes(id);

  const toggleSelectOne = (letter: LetterDTO) => {
    if (isHandedOverStatus(letter.status)) return; // do nothing if already handed over
    setSelectedLetterIds(prev =>
      prev.includes(letter.id) ? prev.filter(x => x !== letter.id) : [...prev, letter.id]
    );
  };

  const openSingleHandover = (id: number) => {
    setSelectedLetterIds([id]); // single selection for handover
    setShowHandoverModal(true);
  };

  const openBulkHandover = () => {
    if (selectedLetterIds.length === 0) {
      showError('Please select at least one letter to handover.');
      return;
    }
    setShowHandoverModal(true);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Letter Management/Letters</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {navItems.map((item, i) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={i}>
                        <Link
                          to={item.link}
                          className={`nav-link px-0 ${locationHook.pathname === item.link ? 'active' : ''}`}
                        >
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h3 className="mb-0">Letters</h3>
                  <div className="d-flex gap-2">
                    {checkPermission('ROLE_HANDOVER_LETTER') && (
                      <button
                        className="btn btn-primary"
                        disabled={selectedLetterIds.length === 0}
                        onClick={openBulkHandover}
                        title={
                          selectedLetterIds.length === 0
                            ? 'Select letters to handover'
                            : `Handover ${selectedLetterIds.length} selected`
                        }
                      >
                        <i className="ti ti-share me-2"></i>Handover Selected
                      </button>
                    )}
                    {checkPermission('ROLE_ADD_LETTER') && (
                      <button className="btn btn-success" onClick={() => setShowAddModal(true)}>
                        <i className="ti ti-plus me-2"></i>Add Letter
                      </button>
                    )}
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">{error}</div>
                ) : letters.length === 0 ? (
                  <div className="alert alert-info" role="alert">No letters found.</div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            {/* Select all on current page (only eligible rows) */}
                            <th style={{ width: 36 }}>
                              <input
                                type="checkbox"
                                aria-label="Select all on this page"
                                onChange={toggleSelectAllOnPage}
                                checked={
                                  letters.filter(l => !isHandedOverStatus(l.status)).length > 0 &&
                                  letters
                                    .filter(l => !isHandedOverStatus(l.status))
                                    .every(l => selectedLetterIds.includes(l.id))
                                }
                              />
                            </th>
                            <th>ID</th>
                            <th>Date</th>
                            <th>Reference No</th>
                            <th>Letter Type</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {letters.map((letter) => {
                            const handedOver = isHandedOverStatus(letter.status);
                            return (
                              <tr key={letter.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    aria-label={`Select letter ${letter.id}`}
                                    checked={isRowSelected(letter.id)}
                                    onChange={() => toggleSelectOne(letter)}
                                    disabled={handedOver}
                                    title={handedOver ? 'Already handed over' : 'Select for handover'}
                                  />
                                </td>
                                <td>{letter.id}</td>
                                <td>{new Date(letter.date).toLocaleDateString()}</td>
                                <td>{letter.referenceNo}</td>
                                <td>{letter.letterType}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      handedOver
                                        ? 'bg-secondary'
                                        : letter.status === 'RECEIVED'
                                        ? 'bg-success'
                                        : 'bg-info'
                                    }`}
                                    title={handedOver ? 'View-only (already handed over)' : undefined}
                                  >
                                    {letter.status}
                                  </span>
                                </td>
                                <td>
                                  <div className="hstack gap-2 fs-3">
                                    {/* View is always allowed */}
                                    {checkPermission('ROLE_VIEW_LETTER') && (
                                      <button
                                        className=" btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                                        onClick={() => {
                                          setSelectedLetterIdForEditView(letter.id);
                                          setShowViewModal(true);
                                        }}
                                        title="View"
                                      >
                                        <i className="ti ti-eye" />
                                      </button>
                                    )}

                                    {/* Edit/Delete allowed only when not handed over (optional rule) */}
                                    {checkPermission('ROLE_ADD_LETTER') && !handedOver && (
                                      <button
                                        className="btn-icon btn-sm btn-soft-info rounded-pill"
                                        onClick={() => {
                                          setSelectedLetterIdForEditView(letter.id);
                                          setShowEditModal(true);
                                        }}
                                        title="Edit"
                                      >
                                        <i className="ti ti-edit" />
                                      </button>
                                    )}

                                    {checkPermission('ROLE_ADD_LETTER') && !handedOver && (
                                      <button
                                        className="btn btn-icon btn-sm btn-soft-danger rounded-pill"
                                        onClick={() => handleDeleteLetter(letter.id)}
                                        title="Delete"
                                      >
                                        <i className="ti ti-trash" />
                                      </button>
                                    )}

                                    {/* Handover button: only when NOT already handed over */}
                                    {checkPermission('ROLE_HANDOVER_LETTER') && !handedOver && (
                                      <button
                                        className="btn btn-icon btn-sm btn-soft-success rounded-pill"
                                        onClick={() => openSingleHandover(letter.id)}
                                        title="Handover this letter"
                                      >
                                        <i className="ti ti-share" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <button
                        className="btn btn-danger"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="btn btn-secondary"
                        disabled={currentPage >= totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <AddLetterModule
              show={showAddModal}
              onHide={() => setShowAddModal(false)}
              onLetterAdded={handleAddLetter}
            />

            {selectedLetterIdForEditView && (
              <>
                <EditLetterModule
                  show={showEditModal}
                  onHide={() => {
                    setShowEditModal(false);
                    setSelectedLetterIdForEditView(null);
                  }}
                  letterId={selectedLetterIdForEditView}
                  onLetterUpdated={handleEditLetter}
                />
                <ViewLetterDetails
                  show={showViewModal}
                  onHide={() => {
                    setShowViewModal(false);
                    setSelectedLetterIdForEditView(null);
                  }}
                  letterId={selectedLetterIdForEditView}
                />
              </>
            )}

            {/* Handover modal – now receives ALL selected IDs */}
            <HandoverLetterModule
              show={showHandoverModal}
              onHide={() => setShowHandoverModal(false)}
              letterIds={selectedLetterIds}
              users={users}
              departments={departments}
              onHandover={handleHandoverLetter}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Letter;
